import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore, apiClient, API_ENDPOINTS } from '@dukene/shared';
import {
  Storefront,
  Palette,
  Globe,
  MagnifyingGlass,
  Sparkle,
  FileText,
  CaretRight,
  CaretLeft,
  CheckCircle,
  WarningCircle,
} from '@phosphor-icons/react';
import { WhatsAppLogo } from '../icons/BrandIcons';

const WhatsAppNavigationIcon = ({ className }: { className?: string; weight?: string }) => (
  <WhatsAppLogo className={className} />
);

export default function StoreLayout() {
  const location = useLocation();
  const { activeStoreId } = useAuthStore();
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth < 768);

  const { data: store } = useQuery<any>({
    queryKey: ['merchant-store', activeStoreId],
    queryFn: async () => apiClient.get<any>(API_ENDPOINTS.MERCHANT.STORE),
    enabled: !!activeStoreId,
  });
  const isPublished = (store as any)?.status === 'ACTIVE';

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 768) {
      setIsCollapsed(true);
    }
  }, [location.pathname]);

  const storeNavigation = [
    {
      label: 'General Identity',
      icon: Storefront,
      href: '/store',
    },
    {
      label: 'Theme & Typography',
      icon: Palette,
      href: '/store/design',
    },
    {
      label: 'Domains & URLs',
      icon: Globe,
      href: '/store/domains',
    },
    {
      label: 'SEO & Social Media',
      icon: MagnifyingGlass,
      href: '/store/seo',
    },
    {
      label: 'Storefront Features',
      icon: Sparkle,
      href: '/store/features',
    },
    {
      label: 'WhatsApp Chat',
      icon: WhatsAppNavigationIcon,
      href: '/store/whatsapp',
    },
    {
      label: 'Legal Policies',
      icon: FileText,
      href: '/store/policies',
    },
  ];

  const isActive = (href: string) => {
    if (href === '/store') {
      return location.pathname === '/store' || location.pathname === '/store/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="flex min-h-[calc(100vh-64px)] bg-white dark:bg-[#1A1D21] rounded-xl border border-[#D2D5D9] dark:border-slate-800 overflow-hidden shadow-sm">
      {/* High-density Polaris Secondary Sidebar */}
      <aside
        className={`border-r border-[#D2D5D9] dark:border-slate-800 bg-[#F6F6F7] dark:bg-[#131517] flex flex-col shrink-0 transition-all duration-200 ${
          isCollapsed ? 'w-14' : 'w-60'
        }`}
      >
        {/* Sidebar Header */}
        <div
          className={`h-14 px-3.5 border-b border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-[#1A1D21] flex items-center ${
            isCollapsed ? 'justify-center' : 'justify-between'
          } gap-2`}
        >
          {!isCollapsed && (
            <div className="min-w-0 overflow-hidden flex items-center gap-2">
              <div className="w-7 h-7 rounded bg-[#E3F1DF] text-[#006838] flex items-center justify-center flex-shrink-0">
                <Storefront weight="fill" className="w-4 h-4" />
              </div>
              <div className="truncate">
                <h2 className="text-xs font-semibold text-[#202223] dark:text-white leading-tight truncate">
                  Online Store
                </h2>
                <p className="text-[10px] text-[#5C5F62] dark:text-slate-400 leading-tight truncate">
                  Storefront Settings
                </p>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="p-1.5 text-[#5C5F62] dark:text-slate-400 hover:text-[#202223] dark:hover:text-white hover:bg-[#E1E4E8] dark:hover:bg-slate-800 rounded transition-colors shrink-0"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <CaretRight weight="bold" className="w-4 h-4" />
            ) : (
              <CaretLeft weight="bold" className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Navigation List */}
        <nav className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {storeNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);

            return (
              <Link
                key={item.href}
                to={item.href}
                title={isCollapsed ? item.label : undefined}
                className={`flex items-center ${
                  isCollapsed ? 'justify-center p-2' : 'gap-2.5 px-3 py-2'
                } rounded-md transition-colors text-xs ${
                  active
                    ? 'bg-white dark:bg-[#1A1D21] border border-[#D2D5D9] dark:border-slate-700 font-semibold text-[#006838] dark:text-emerald-400 shadow-sm'
                    : 'border border-transparent text-[#202223] dark:text-slate-300 hover:bg-[#EBEBEB] dark:hover:bg-slate-800'
                }`}
              >
                <Icon
                  weight={active ? 'fill' : 'bold'}
                  className={`w-4 h-4 flex-shrink-0 ${
                    active ? 'text-[#006838] dark:text-emerald-400' : 'text-[#5C5F62] dark:text-slate-400'
                  }`}
                />
                {!isCollapsed && (
                  <span className="truncate flex-1">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer Status Badge */}
        <div className="p-2.5 border-t border-[#D2D5D9] dark:border-slate-800 bg-white dark:bg-[#1A1D21]">
          <div
            className={`px-2.5 py-1.5 rounded-md flex items-center ${
              isCollapsed ? 'justify-center' : 'gap-2'
            } ${
              isPublished
                ? 'bg-[#E3F1DF] border border-[#B3DEC1] text-[#006838]'
                : 'bg-[#FFF4E5] border border-[#FFD79E] text-[#B95000]'
            }`}
          >
            {isPublished ? (
              <CheckCircle weight="fill" className="w-3.5 h-3.5 shrink-0" />
            ) : (
              <WarningCircle weight="fill" className="w-3.5 h-3.5 shrink-0" />
            )}
            {!isCollapsed && (
              <span className="text-[11px] font-semibold truncate">
                {isPublished ? 'Storefront Published' : 'Reserved / Offline'}
              </span>
            )}
          </div>
        </div>
      </aside>

      {/* Content area */}
      <main className="flex-1 overflow-y-auto bg-white dark:bg-[#1A1D21]">
        <div className="p-4 sm:p-6 md:p-8 max-w-5xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
